// ── Check-in snooze sync ─────────────────────────────────────────────────────
// TonightMode.jsx's "did you watch this yet" evening check-in has a "Save it for
// later" snooze that used to be localStorage-only, never synced to the account —
// same bug class as coachmarks (2026-08-17) and the What's New seen-version flag
// (2026-08-18). Snoozing on one device did nothing on another, so the same prompt
// could fire twice in one evening (bug report 2026-08-21, two devices <20 min apart).
//
// Lives in its own file (not inside TonightMode.jsx) so useAuth.jsx — an
// entry-chunk module — can import syncSnoozeFromProfile() without dragging in
// TonightMode's component code.
import { supabase } from './supabase'
import { todayKey } from './datetime'

const SNOOZE_KEY = 'nextep_checkin_snooze'

function readSnooze() {
  try { return JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}') } catch { return {} }
}

export function isSnoozedToday(planId) {
  return readSnooze()[planId] === todayKey()
}

export function snoozeToday(planId) {
  const today = todayKey()
  try {
    const m = readSnooze()
    m[planId] = today
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(m))
  } catch { /* non-fatal */ }
  // Fire-and-forget: the RPC prunes to only today's entries server-side, so
  // ui_prefs never accumulates old snoozes (a snooze is only ever meaningful for
  // the day it was made).
  try { supabase.rpc('mark_checkin_snoozed', { p_plan_id: planId, p_date: today }).then(() => {}, () => {}) } catch { /* non-fatal */ }
}

// Called once when the profile loads (useAuth.jsx) — pulls today's snoozes made on
// ANY device into this device's localStorage, so a second device doesn't re-nag
// for a plan already snoozed elsewhere today. Only merges entries matching TODAY;
// the RPC already prunes stale dates server-side, but this guards against a
// device whose local clock lags a day boundary.
export function syncSnoozeFromProfile(profile) {
  const remote = profile?.ui_prefs?.checkin_snooze
  if (!remote || typeof remote !== 'object') return
  const today = todayKey()
  try {
    const local = readSnooze()
    let changed = false
    for (const [planId, date] of Object.entries(remote)) {
      if (date === today && local[planId] !== today) { local[planId] = today; changed = true }
    }
    if (changed) localStorage.setItem(SNOOZE_KEY, JSON.stringify(local))
  } catch { /* non-fatal */ }
}
